import { useEffect, useRef } from 'react';

/**
 * Automatically re-fetches data on:
 *  - initial mount
 *  - tab becoming visible again (user switched back)
 *  - window gaining focus (user clicked back into browser)
 *  - a periodic interval while the tab is active
 *
 * Polling pauses when the tab is hidden so no wasted API calls.
 * Uses a ref internally so callers don't need useCallback.
 */
export function useAutoRefresh(fetchFn, intervalMs = 30000) {
  const fnRef = useRef(fetchFn);
  fnRef.current = fetchFn;

  useEffect(() => {
    let timer;

    const call = () => Promise.resolve(fnRef.current()).catch(() => {});

    const tick = () => {
      if (document.visibilityState !== 'hidden') call();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        call();
        clearInterval(timer);
        timer = setInterval(tick, intervalMs);
      } else {
        clearInterval(timer);
      }
    };

    const onFocus = () => call();

    call();
    timer = setInterval(tick, intervalMs);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, [intervalMs]);
}